import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link } from "react-router-dom"
import { OperationsAdminVenueTalentTabs } from "src/components/operations/administrator/talent/OperationsAdminVenueTalentTabs"
import { CreateOperationsAdminVenueTalentBlackoutAction, UpdateOperationsAdminVenueTalentBlackoutAction } from "src/components/operations/administrator/talent/blackouts/OperationsAdminVenueTalentBlackoutActions"
import { DataTable } from "src/components/ui/DataTable"
import { DrawerPageHeader } from "src/components/ui/DrawerPageHeader"
import { DrawerPageLayout } from "src/components/ui/DrawerPageLayout"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { BlackoutFragment, Blackouts_Bool_Exp } from "src/state/gql/_types"
import { formatDate } from "src/utils/dates"

export function OperationsAdminVenueTalentBlackoutsOverlay() {

    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.blackouts)
    const {
        subscribeToItems,
        unsubscribeFromItems,
        getMoreItems,
        resetModule,
        setMeta,
        selectItem
    } = useActions().blackouts
    const { view } = useAppState(state => state.ui)
    const { currentItemId: currentUserId } = useAppState(state => state.users)

    const {
        filters,
        filterParams
    } = usePageFilters<Blackouts_Bool_Exp['_and'], BlackoutFragment>({
        overlayMode: true,
        baseFilters: [{
            talent_id: { _eq: currentUserId }
        }],
        clickFilters: [],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'show',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'active',
        clickFilterValues: {
            upcoming: { label: 'Upcoming', filter: [{ end_at: { _gte: new Date().toISOString() } }] },
            past: { label: 'Past', filter: [{ end_at: { _lte: new Date().toISOString() } }] },
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<BlackoutFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor('description', {
            header: 'Description'
        }),
        columnHelper.accessor('start_at', {
            header: 'Starts At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY h:mm A')
        }),
        columnHelper.accessor('end_at', {
            header: 'Ends At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY h:mm A')
        }),
        columnHelper.accessor('created_at', {
            header: 'Created At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
        columnHelper.accessor('updated_at', {
            header: 'Updated At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<BlackoutFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        selectItem(id)
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/blackouts/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.id}</Text>
                                        <Badge variant="outline">{i.description}</Badge>
                                    </Group>
                                    <Text size="sm">{i.id}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<BlackoutFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <DrawerPageLayout
            header={
                <DrawerPageHeader
                    title={`Blackout Dates`}
                    renderAction={
                        <Group>
                            <UpdateOperationsAdminVenueTalentBlackoutAction />
                            <CreateOperationsAdminVenueTalentBlackoutAction />
                        </Group>
                    }
                />
            }
        >
            <OperationsAdminVenueTalentTabs overlay={true}/>
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Blackouts' />)}
        </DrawerPageLayout>
    )

}